/**
 * Everybody in the venue, as level data: the four poses, what they are wearing,
 * and the two of them who move out of the way.
 *
 * Only the sleepers are hashed - they are boxes the run has to get past. The
 * rest is dressing, which is why a stander can be re-posed or re-shirted
 * without retiring a leaderboard time. See `src/render/People.ts` for what
 * these turn into.
 */

import type { Box } from './levelBox';
import { ext } from './levelAuthoring';

export interface SleeperSpec {
  name: string;
  center: [number, number];
  /** Which world axis the body lies along. */
  orientation: 'x' | 'z';
  length: number;
  width: number;
  height: number;
  /** Height the body rests at. Non-zero for somebody asleep in a bed. */
  y?: number;
}

/**
 * Somebody sitting down, drawn and nothing else.
 *
 * A sleeper is a collider with a body drawn round it. This is the other way
 * round: there is no box here at all, because the only thing sitting on this
 * level sits in a corner nobody runs through, and the hall's own rule is that
 * what stands where nobody runs is built out of dressing - so it stays outside
 * the fingerprint and cannot retire a leaderboard time. See
 * `src/config/CLAUDE.md`.
 *
 * The pose comes from two numbers rather than from a rig. `height` is what he
 * would measure standing up and every proportion is a fraction of it;
 * `seatHeight` is what the bench under him puts his hips at, which is the one
 * thing the body cannot derive for itself - the feet are on the floor whatever
 * the seat does, so the seat decides where the knee ends up.
 */
export interface SitterSpec {
  name: string;
  /** Where he sits: the point on the seat his hips are over. */
  seat: [number, number];
  /** Top of the bench under him. */
  seatHeight: number;
  /** Standing height, which sets every proportion of the figure. */
  height: number;
  /** Which way he faces, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  /** The cap he has on, if he has one. */
  cap?: SitterCap;
}

/**
 * Somebody down on the floor, drawn and nothing else.
 *
 * The third kind of person in the venue, and the only one who comes in two
 * poses - because there are two ways of ending a night on a floor and they do
 * not look remotely alike. `pose` picks which, and the difference is the
 * figure rather than a detail of it. A sleeper is a box with a body laid flat
 * on it and a sitter is a body sat upright on a bench; neither of those is a
 * man who went down where he was standing.
 *
 * This spec is render-only. The tent audience has matching invisible solids
 * derived from these poses in config/level.ts; those boxes are fingerprinted.
 * The folded figure in the pirtti remains decorative.
 *
 * Two numbers place him whichever pose he is in, the same two a sitter is
 * placed by: where his hips are, and which way he is pointed. Everything else
 * is a fraction of `height`.
 */
export interface SprawlerSpec {
  name: string;
  /** The floor under his hips, and the point the rest of him is laid out from. */
  hips: [number, number];
  /** Which way his head points, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  /** Standing height, which sets every proportion of him. */
  height: number;
  /**
   * How he went down. `folded` unless it says otherwise, because that is the
   * pose this type was written for.
   *
   * `folded` is face down over his own knees with his hips the highest part of
   * him and his trousers where they ended up. It is a joke about one man, it
   * is told once, and `TUNTEMATON` is the man it is told about.
   *
   * `flat` is the other thing a floor gets used for: out cold along the whole
   * length of himself, dressed, with nothing about him that has to be looked
   * at twice. It is what everybody who is merely asleep on the ground is,
   * because the first pose seven times over stops being one man's bad night
   * and becomes what the game thinks a party looks like.
   */
  pose?: 'folded' | 'flat';
}

/** Lettering on the front of a cap, read the same way a sign is. */
export interface SitterCap {
  text: string;
  /** The letters. */
  color: string;
  /** The cap itself - the crown, the panel they are stitched on and the peak. */
  background: string;
}

/**
 * Lettering across the front of a shirt, read exactly the way a cap's is.
 *
 * A separate struct from `SitterCap` only because `background` means a
 * different thing here: a cap is cloth the figure is *given*, and a shirt is
 * cloth the figure already had. So this colour does not just back the letters,
 * it replaces the shirt colour the palette would otherwise have rolled - print
 * and cloth have to match or the word sits on a patch.
 */
export interface ShirtPrint {
  text: string;
  /** The letters. */
  color: string;
  /** The shirt they are printed on, which is the shirt he wears. */
  background: string;
}

/**
 * Somebody on his feet, drawn and nothing else: the fourth kind of person in
 * the venue, and the only one standing up who is not Hannu.
 *
 * The other three are each defined by what they are lying or sitting on, which
 * is why a sleeper carries a bed height and a sitter a bench height. This one
 * is on the ground like the player is, so all it takes is where his feet are
 * and which way he is looking - everything else is a fraction of `height`.
 *
 * This spec is render-only. People who block the runner also have collision
 * geometry: the tent fans have fixed invisible solids derived from their
 * positions and heights. Their animation does not move those boxes.
 */
export interface StanderSpec {
  name: string;
  /** The ground under his feet, in world XZ. */
  stand: [number, number];
  /** Standing height, which sets every proportion of him. */
  height: number;
  /** Which way he faces, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  /** The cap he has on, if he has one. */
  cap?: SitterCap;
  /**
   * A pair of cans over his ears, if he has them.
   *
   * Mutually exclusive with `cap` in practice - a man cannot wear both, and
   * nothing here enforces it because nothing here needs to. Its own flag
   * rather than a third `SitterCap` variant because a cap carries lettering
   * and this does not: headphones say what somebody is *doing*, which is the
   * one thing about the man in the tent that has to read from the lot.
   */
  headphones?: boolean;
  /** The lettering across his shirt, if he has any. */
  shirt?: ShirtPrint;
  /**
   * Both arms over his head, if what he is standing in front of is worth it.
   *
   * Mutually exclusive with `points` the way `headphones` is with `cap`: one
   * arm cannot be both aimed at something and in the air, and nothing here
   * enforces it because nothing needs to - `World` poses the aimed arm and
   * leaves the other where this put it.
   *
   * A flag where `points` is a coordinate, and the difference is the whole
   * reason it is not simply a point somewhere over the tent: an arm up at a
   * disco is not aimed at anything. A target invented in mid-air to lift it
   * with would be a number picked for the angle it happens to give, which is
   * exactly what `points` was written to stop anybody doing. So the angles
   * these arms are held at are chosen, and they are chosen once - in `World`,
   * beside the bend the pointing arm keeps.
   */
  cheering?: boolean;
  /**
   * What his raised arm is aimed at, in world XYZ. Without one, both arms hang.
   *
   * A point rather than an angle, and a point held apart from `rotationY`,
   * because the two answer different questions: which way a man is turned, and
   * what he is pointing at. `World` works the arm out from the difference - so
   * a figure may point at something he is not square to, and a figure that *is*
   * square to it says so by deriving both from the same coordinate.
   *
   * The height is in it for the same reason the other two are: a man pointing
   * at a machine on a desk is pointing *down* at it, and the difference
   * between that and the same arm a few degrees up is the difference between
   * showing somebody a chair and saluting. So the arm is aimed at the thing
   * itself rather than lifted by an angle somebody liked the look of.
   */
  points?: [number, number, number];
}

/**
 * Mäysä from the ruokatiimi, and the two places he stands.
 *
 * A `StanderSpec` with a second pose bolted on, because he *is* one of the
 * standers in every way that matters to the renderer - one body, one cap, one
 * printed shirt - and the only thing unusual about him is that he moves once,
 * on the step the plate fills.
 *
 * One figure and two poses rather than two figures, so that the man who steps
 * aside is recognisably the man who was in the way: `standerFigure` rolls a
 * face and a skin tone off its own seed, and two entries would be two
 * different people wearing the same shirt.
 *
 * Only `gate` is hashed, and only because it names a collider. Where he stands
 * and what he points at are dressing - the box is what stops anybody.
 */
export interface MaysaSpec extends StanderSpec {
  /** The solid that is him while he is filling the porstua. Toggled by `Aamupala`. */
  gate: string;
  /**
   * The man asleep across the mouth of the porch, by name.
   *
   * Nothing in the simulation reads this - he is a collider whether Mäysä is
   * there or not. It is what `Game` measures the *shout* off: the runner has
   * not tried this door until he is over that man, and a line explaining a
   * wall is noise said anywhere else. Named rather than written out as a line
   * in z, so that sliding the sleeper slides the moment Mäysä opens his mouth.
   * See `MAYSA_LINE`.
   */
  doorstep: string;
  /** Where he goes once the plate is full: out of the doorway, pointing into it. */
  aside: {
    stand: [number, number];
    rotationY: number;
    points: [number, number, number];
  };
}

/**
 * One of the players in the way, and the second place he stands.
 *
 * A `StanderSpec` with an `aside` pose bolted on, which is precisely what
 * `MaysaSpec` is - and deliberately not the same interface, because Mäysä
 * carries two things nobody in this crowd has: his own gate, and the sleeper
 * that decides when he speaks. What is shared is the idea, not the struct.
 *
 * Each of them keeps his own face and his own shirt across the move, because
 * he is one figure that is moved rather than two figures swapped: `World`
 * rolls a face off the seed a figure is built with, and a second entry would
 * be a second person wearing the first one's clothes.
 */
export interface PorukkaSpec extends StanderSpec {
  /** Where he stands once the tuoppi is full: off the runway, and looking at it. */
  aside: {
    stand: [number, number];
    rotationY: number;
    points?: [number, number, number];
  };
}

/** Turns a sleeping-body spec into its axis-aligned collider box. */
export function sleeperBox(spec: SleeperSpec): Box {
  const [cx, cz] = spec.center;
  const halfLength = spec.length / 2;
  const halfWidth = spec.width / 2;
  const halfX = spec.orientation === 'x' ? halfLength : halfWidth;
  const halfZ = spec.orientation === 'x' ? halfWidth : halfLength;
  return ext(cx - halfX, cz - halfZ, cx + halfX, cz + halfZ, spec.height, spec.y ?? 0);
}
